import React from 'react'
import { useTranslation } from 'react-i18next'
import { FormControlLabel, RadioGroup, Radio } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material'
import { Controller } from 'react-hook-form'
import type { Control as ControlProps } from 'react-hook-form'
import { StackItemProps, StackItem } from 'ui/layout/StackItem'
import { Caption } from './Texts'

const useStyles = makeStyles()((theme: Theme) => ({
  item: {
    paddingRight: theme.spacing(3),
    marginLeft: 0,
  },
  verticalItem: { paddingRight: 0, marginRight: 0 },
  radio: {
    paddingLeft: 0,
  },
  description: {
    padding: theme.spacing(0, 0, 1, 3.6),
  },
  // NOTE: スマホレイアウトでの崩れ修正
  hasDescriptionLabel: {
    paddingTop: theme.spacing(1),
  },
  hasDescriptionRadio: {
    paddingTop: 0,
    paddingBottom: 0,
  },
}))

export type StackItemRadioSelectProps<T = string> = {
  /**
   *  ReactHookFormで使うname
   */
  name: string
  /**
   *
   */
  values?: {
    label: React.ReactNode
    value: T
    description?: React.ReactNode
  }[]
  /**
   * エラーがある時はエラーメッセージ
   */
  errorMessage?: React.ReactNode
  /**
   * フォームに最初に入っている値
   */
  defaultValue?: T
  /**
   * ReactHookFormに必須のオブジェクト
   */
  control: ControlProps
  /**
   * Controlにセットするイベントハンドラ
   */
  onChange?: (obj: any) => void
  /**
   * 縦並びにする
   */
  vertical?: boolean
} & StackItemProps

/**
 * StackItem + TextField + ReactHookForm 通常使用のショートハンド
 */
export const StackItemRadioSelect = <T,>({
  label,
  labelHelper,
  required,
  values,
  errorMessage,
  control,
  defaultValue,
  name,
  description,
  alignTop,
  onChange,
  labelStatus,
  borderBottom,
  borderTop,
  children,
  vertical,
}: StackItemRadioSelectProps<T>) => {
  const { classes, cx } = useStyles()
  const { t } = useTranslation()
  if (!values) {
    // NOTE: props.DefaultValueだとlabelがi18n.t()となり、非同期で言語が切り替わらないのでここでt()に渡すようにした
    values = [
      {
        label: t('必須'),
        value: 'required' as unknown as T,
      },
      {
        label: t('任意'),
        value: 'optional' as unknown as T,
      },
      {
        label: t('使用しない'),
        value: 'not' as unknown as T,
      },
    ]
  }
  return (
    <StackItem
      required={required}
      labelStatus={labelStatus}
      labelHelper={labelHelper}
      label={label}
      description={
        errorMessage ? <Caption error>{errorMessage}</Caption> : description
      }
      alignTop={alignTop}
      borderBottom={borderBottom}
      borderTop={borderTop}
    >
      <Controller
        control={control}
        name={name}
        defaultValue={defaultValue}
        render={({ field }) => (
          <RadioGroup
            {...field}
            row={!vertical}
            onChange={(event, value) => {
              const valueType = typeof values?.[0]?.value
              onChange?.(value)
              if (valueType === 'number') {
                field.onChange(Number(value))
              } else if (valueType === 'boolean') {
                field.onChange(value === 'true')
              } else {
                field.onChange(event)
              }
            }}
          >
            {values &&
              values.map((v, index) => (
                <div key={`${v.value}-${index}`}>
                  <FormControlLabel
                    value={v.value}
                    control={
                      <Radio
                        className={cx(
                          classes.radio,
                          v.description ? classes.hasDescriptionRadio : null
                        )}
                        color={'primary'}
                      />
                    }
                    label={v.label}
                    className={cx(
                      classes.item,
                      vertical && classes.verticalItem,
                      v.description ? classes.hasDescriptionLabel : null
                    )}
                  />
                  {v.description && (
                    <div className={classes.description}>{v.description}</div>
                  )}
                </div>
              ))}
          </RadioGroup>
        )}
      />
      {children}
    </StackItem>
  )
}
